import type { StructuredLogTree } from '@repo/sdk'
import { mergeStructuredLogDelta } from '@repo/sdk'
import type { RetryAffordance } from '@/lib/game-retry-card'

export type PlayStatusTransport = 'compact' | 'legacy'

export type CompactFallbackReason
  = | 'bootstrap_failed'
    | 'disabled'
    | 'malformed_snapshot'
    | 'request_failed'
    | 'recorded_tournament'

export function resolveCompactStatusEnabled(
  env: Record<string, unknown> = import.meta.env as Record<string, unknown>,
): boolean {
  try {
    return env.VITE_COMPACT_STATUS_ENABLED === 'true'
  }
  catch {
    return false
  }
}

export interface CompactSnapshot {
  game?: {
    id?: string
    tournamentEditionId?: string | null
    tournamentState?: string | null
    tournamentCompetitionOpen?: boolean
    tournamentCanContinue?: boolean
    retryLater?: boolean
    retry?: RetryAffordance
    currentRound?: number
    phase?: string
    shouldHideSubphaseDetail?: boolean
    status?: string
    subPhase?: string
    winner?: string | null
  }
  humanPlayer?: unknown
  logDelta?: {
    entries?: unknown[]
    revision?: number
    since?: number
    source?: string
  }
  nextInput?: unknown
  openingKnownTeammates?: Array<{ seatId: string, role: string }>
  roleBoard?: unknown | null
  seats?: Array<{
    id?: string
    deathCause?: unknown[]
    faction?: string | null
    isDead?: boolean
    seatState?: 'human' | 'inherited' | 'delegated' | 'ai'
    isSheriff?: boolean
    isThinking?: boolean
    role?: string | null
    status?: string
  }>
  statusRevision?: number
  statusVersion?: string
  turnClock?: {
    serverNowMs: number
    nextTakeoverAtMs: number
  } | null
  viewer?: {
    mode?: 'owner' | 'shared' | 'observer' | 'god'
    canSeeGodView?: boolean
    canSeeFullReplay?: boolean
    ownedSeatIds?: string[]
    perspectiveSeatId?: string | null
  }
  waitingOn?: Array<{
    id: string
    name: string
    position: number
    takeoverAtMs?: number
  }>
}

export interface CompactBootstrap {
  bootstrapVersion?: string
  game?: {
    id?: string
    gameSetting?: string
    gameType?: string
    language?: string
  }
  replayI18n?: Record<string, string>
  roomContext?: { roomId: string, isHost: boolean } | null
  seats?: Array<{
    id?: string
    createdAt?: unknown
    modelDisplayName?: string
    modelName?: string
    name?: string
    position?: number
  }>
}

export function isUsableCompactSnapshot(
  snapshot: unknown,
): snapshot is CompactSnapshot {
  if (!snapshot || typeof snapshot !== 'object')
    return false
  const candidate = snapshot as CompactSnapshot
  const { turnClock } = candidate
  const hasValidTurnClock
    = turnClock === null
      || turnClock === undefined
      || (typeof turnClock === 'object'
        && Number.isFinite(turnClock.serverNowMs)
        && Number.isFinite(turnClock.nextTakeoverAtMs))
  const hasValidWaitingOn
    = candidate.waitingOn === undefined
      || (Array.isArray(candidate.waitingOn)
        && candidate.waitingOn.every(
          waiting =>
            waiting !== null
            && waiting !== undefined
            && typeof waiting.id === 'string'
            && typeof waiting.name === 'string'
            && Number.isFinite(waiting.position)
            && (waiting.takeoverAtMs === undefined
              || Number.isFinite(waiting.takeoverAtMs)),
        ))
  const hasValidOpeningTeammates
    = candidate.openingKnownTeammates === undefined
      || (Array.isArray(candidate.openingKnownTeammates)
        && candidate.openingKnownTeammates.every(
          teammate =>
            teammate !== null
            && typeof teammate.seatId === 'string'
            && typeof teammate.role === 'string',
        ))
  return (
    typeof candidate.game?.status === 'string'
    && Array.isArray(candidate.seats)
    && typeof candidate.statusVersion === 'string'
    && typeof candidate.logDelta?.revision === 'number'
    && hasValidTurnClock
    && hasValidWaitingOn
    && hasValidOpeningTeammates
  )
}

export function isUsableBootstrap(
  bootstrap: unknown,
): bootstrap is CompactBootstrap {
  if (!bootstrap || typeof bootstrap !== 'object')
    return false
  const candidate = bootstrap as CompactBootstrap
  return (
    Array.isArray(candidate.seats)
    && typeof candidate.game?.language === 'string'
    && typeof candidate.replayI18n === 'object'
    && candidate.replayI18n !== null
  )
}

export interface AdaptedPlayStatus {
  game: Record<string, unknown>
  humanPlayer: unknown
  logRevision: number
  nextInput: unknown
  openingKnownTeammates: Array<{ seatId: string, role: string }>
  players: Array<Record<string, unknown>>
  roleBoard: unknown | null
  roomContext: { roomId: string, isHost: boolean } | null
  statusRevision: number
  statusVersion: string
  transport: PlayStatusTransport
  turnClock?: {
    serverNowMs: number
    nextTakeoverAtMs: number
  } | null
  viewer: {
    mode: 'owner' | 'shared' | 'observer' | 'god'
    canSeeGodView: boolean
    canSeeFullReplay?: boolean
    ownedSeatIds: string[]
    perspectiveSeatId: string | null
  }
  waitingOn: Array<{
    id: string
    name: string
    position: number
    takeoverAtMs?: number
  }>
}

export function mergeCompactLog(
  cachedLog: StructuredLogTree | null,
  snapshot: CompactSnapshot,
): StructuredLogTree {
  return mergeStructuredLogDelta(cachedLog, {
    entries: (snapshot.logDelta?.entries ?? []) as never,
    revision: snapshot.logDelta?.revision ?? 0,
    since: snapshot.logDelta?.since ?? 0,
  })
}

export function adaptCompactSnapshot(input: {
  bootstrap: CompactBootstrap
  log: StructuredLogTree
  snapshot: CompactSnapshot
}): AdaptedPlayStatus {
  const { bootstrap, log, snapshot } = input

  const staticSeats = new Map(
    (bootstrap.seats ?? []).map(seat => [seat.id, seat] as const),
  )
  const isGodView = snapshot.viewer?.canSeeGodView === true
  const ownedSeatIds = snapshot.viewer?.ownedSeatIds ?? []
  const viewerMode
    = snapshot.viewer?.mode
      ?? (isGodView ? 'god' : ownedSeatIds.length > 0 ? 'owner' : 'observer')
  const perspectiveSeatId
    = snapshot.viewer?.perspectiveSeatId
      ?? (viewerMode === 'owner' ? (ownedSeatIds[0] ?? null) : null)

  return {
    game: {
      id: snapshot.game?.id ?? bootstrap.game?.id,
      currentRound: snapshot.game?.currentRound,
      tournamentEditionId: snapshot.game?.tournamentEditionId ?? null,
      tournamentState: snapshot.game?.tournamentState ?? null,
      tournamentCompetitionOpen: snapshot.game?.tournamentCompetitionOpen ?? false,
      tournamentCanContinue: snapshot.game?.tournamentCanContinue ?? false,
      retryLater: snapshot.game?.retryLater ?? false,
      retry: snapshot.game?.retry ?? null,
      data: null,
      gameSetting: bootstrap.game?.gameSetting,
      godLog: '',
      godLogJson: isGodView ? log : null,
      language: bootstrap.game?.language,
      phase: snapshot.game?.phase,
      replayI18n: bootstrap.replayI18n ?? {},
      shouldHideSubphaseDetail:
          snapshot.game?.shouldHideSubphaseDetail ?? false,
      status: snapshot.game?.status,
      subPhase: snapshot.game?.subPhase,
      summaryLog: null,
      winner: snapshot.game?.winner ?? null,
    },
    humanPlayer: snapshot.humanPlayer ?? null,
    logRevision: snapshot.logDelta?.revision ?? 0,
    nextInput: snapshot.nextInput ?? null,
    openingKnownTeammates: snapshot.openingKnownTeammates ?? [],
    players: (snapshot.seats ?? []).map((seat) => {
      const staticSeat = staticSeats.get(seat.id)
      const receivesPlayerJournal
        = !isGodView
          && (ownedSeatIds.includes(seat.id ?? '')
            || (viewerMode === 'shared' && perspectiveSeatId === seat.id))
      return {
        id: seat.id,
        createdAt: staticSeat?.createdAt,
        data: null,
        deathCause: seat.deathCause ?? [],
        faction: seat.faction ?? null,
        gameId: bootstrap.game?.id,
        isDead: seat.isDead ?? false,
        isHuman: seat.seatState === 'human',
        seatState: seat.seatState,
        isSheriff: seat.isSheriff ?? false,
        isThinking: seat.isThinking ?? false,
        log: receivesPlayerJournal ? '' : null,
        modelDisplayName: staticSeat?.modelDisplayName,
        modelName: staticSeat?.modelName,
        name: staticSeat?.name,
        playerJsonLog: receivesPlayerJournal ? log : null,
        position: staticSeat?.position,
        role: seat.role ?? null,
        status: seat.status,
        updatedAt: staticSeat?.createdAt,
      }
    }),
    roleBoard: snapshot.roleBoard ?? null,
    roomContext: bootstrap.roomContext ?? null,
    statusRevision: snapshot.statusRevision ?? 0,
    statusVersion: snapshot.statusVersion ?? '',
    transport: 'compact',
    turnClock: snapshot.turnClock ?? null,
    viewer: {
      mode: viewerMode,
      canSeeGodView: isGodView,
      canSeeFullReplay: snapshot.viewer?.canSeeFullReplay ?? true,
      ownedSeatIds,
      perspectiveSeatId,
    },
    waitingOn: snapshot.waitingOn ?? [],
  }
}

export function classifyCompactFallback(input: {
  bootstrap?: unknown
  bootstrapError?: unknown
  enabled: boolean
  snapshot?: unknown
  snapshotError?: unknown
}): CompactFallbackReason | null {
  if (!input.enabled)
    return 'disabled'
  if (input.bootstrapError)
    return 'bootstrap_failed'
  if (input.snapshotError)
    return 'request_failed'
  if (input.bootstrap !== undefined && !isUsableBootstrap(input.bootstrap))
    return 'bootstrap_failed'
  if (input.snapshot !== undefined && !isUsableCompactSnapshot(input.snapshot))
    return 'malformed_snapshot'
  return null
}
